import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { showToast } from '@repo/ui';

import { uploadApi } from '@xtnd/web/features/uploads/api';

import { arMatterApi } from '../api';
import { FileCategory } from '../types';
import { getApiErrorMessage } from '../utils/apiErrorHandler';

const attemptAttachmentsKey = (matterId: string, clientId: string, sk: string) =>
  ['attempt-attachments', matterId, clientId, sk] as const;

const matterAttemptsKey = (matterId: string, clientId: string) =>
  ['matter', 'attempts', matterId, clientId] as const;

export async function uploadAttemptAttachmentFile(
  matterId: string,
  clientId: string,
  sk: string,
  file: File
) {
  const { data: presigned } = await uploadApi.createPresignedUrl({
    fileName: file.name,
    contentType: file.type,
  });

  const put = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!put.ok) throw new Error('S3 PUT failed');

  return arMatterApi.registerAttemptAttachment(matterId, clientId, sk, {
    key: presigned.key,
    name: file.name,
    size: file.size,
    type: file.type,
    category: FileCategory.COLLECTION_ATTEMPT,
  });
}

export const useAttemptAttachmentsQuery = (
  matterId: string,
  clientId: string,
  sk: string,
  options: { enabled?: boolean } = {}
) =>
  useQuery({
    queryKey: attemptAttachmentsKey(matterId, clientId, sk),
    queryFn: () => arMatterApi.getAttemptAttachments(matterId, clientId, sk),
    enabled: (options.enabled ?? true) && !!sk,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

export const useUploadAttemptAttachment = (
  matterId: string,
  clientId: string,
  sk: string
) => {
  return useMutation({
    mutationFn: (file: File) => uploadAttemptAttachmentFile(matterId, clientId, sk, file),
    onError: err => showToast.error(getApiErrorMessage(err, 'Failed to upload attachment.')),
  });
};

export const useRemoveAttemptAttachment = (
  matterId: string,
  clientId: string,
  sk: string
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => arMatterApi.removeAttemptAttachment(matterId, clientId, sk, key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attemptAttachmentsKey(matterId, clientId, sk) });
      qc.invalidateQueries({ queryKey: matterAttemptsKey(matterId, clientId) });
      showToast.success('Attachment removed');
    },
    onError: err => showToast.error(getApiErrorMessage(err, 'Failed to remove attachment.')),
  });
};

export const useAttemptAttachmentDownloadUrl = (
  matterId: string,
  clientId: string,
  sk: string
) => {
  return async (key: string): Promise<string> => {
    const { data } = await arMatterApi.getAttemptAttachmentDownloadUrl(
      matterId,
      clientId,
      sk,
      key
    );
    return data.url;
  };
};
